import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Tooltip, CircularProgress } from '@material-ui/core'
import { ACCENT, CONDENSED } from './rackStyles'
import COLORS from '../../../utils/constants/colors'

const useStyles = makeStyles({
  // Centred on the stage's right edge, over the video. The frame ignores pointer events so the
  // video underneath stays reachable; the rail and its open flyout take them back.
  anchor: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 30,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    pointerEvents: 'none',
  },
  rail: {
    pointerEvents: 'auto',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.14)',
    background: 'rgba(17, 18, 20, 0.9)',
    overflow: 'hidden',
  },
  button: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    borderTop: '1px solid rgba(255, 255, 255, 0.12)',
    background: 'transparent',
    color: '#c8ccd2',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background 140ms ease-out, color 140ms ease-out',
    '&:first-child': { borderTop: 'none' },
    '&:hover': { background: 'rgba(255, 255, 255, 0.08)', color: '#fff' },
  },
  // A toggle that is on.
  // An open flyout, a tool with something on air, or a toggle that is on: one lit look.
  buttonLive: {
    background: 'rgba(255, 123, 91, 0.2)',
    color: ACCENT,
    '&:hover': { background: 'rgba(255, 123, 91, 0.28)', color: ACCENT },
  },
  // Something is on air that is not the operator's: lit, but never in their orange.
  buttonNeutral: {
    background: 'rgba(255, 255, 255, 0.14)',
    color: '#f2f3f5',
    '&:hover': { background: 'rgba(255, 255, 255, 0.2)', color: '#f2f3f5' },
  },
  badge: {
    position: 'absolute',
    top: '6px',
    right: '5px',
    minWidth: '16px',
    height: '16px',
    padding: '0 4px',
    boxSizing: 'border-box',
    borderRadius: '8px',
    background: ACCENT,
    color: '#1a0d08',
    fontSize: '10.5px',
    fontWeight: 600,
    lineHeight: '16px',
    textAlign: 'center',
    pointerEvents: 'none',
  },
  buttonAmber: {
    background: COLORS.darkYellow,
    color: COLORS.black,
    '&:hover': { background: COLORS.darkYellow, color: COLORS.black },
  },
  buttonDisabled: {
    opacity: 0.45,
    cursor: 'default',
    '&:hover': { background: 'transparent', color: '#c8ccd2' },
  },
  flyout: {
    pointerEvents: 'auto',
    width: '312px',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.14)',
    background: 'rgba(17, 18, 20, 0.9)',
    boxShadow: '0 14px 40px rgba(0, 0, 0, 0.45)',
    overflow: 'hidden',
  },
  flyoutHeader: {
    flex: 'none',
    padding: '14px 14px 10px',
    fontFamily: CONDENSED,
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#9aa1aa',
  },
  flyoutBody: { minHeight: 0, display: 'flex', flexDirection: 'column' },
  pill: {
    padding: '6px 10px',
    borderRadius: '8px',
    background: 'rgba(17, 18, 20, 0.92)',
    color: '#f2f3f5',
    fontSize: '12.5px',
    fontWeight: 600,
  },
})

/**
 * The operator's tools, as one column of icons at the stage's right edge. An item either acts
 * at once (`onPress`) or opens a flyout to its left (`content`), one flyout at a time. An item
 * this game cannot use stays in the rail, dimmed, and says why on hover.
 * A toggle also shows whether it is on (`on`).
 */
const StudioRail = ({ items }) => {
  const classes = useStyles()
  const [openKey, setOpenKey] = useState(null)
  const open = items.find((item) => item.key === openKey && item.content && !item.disabled) ?? null

  const press = (item) => {
    // A busy toggle ignores presses; a busy tool with a flyout still opens, so what is in
    // progress inside it can be stopped.
    if (item.disabled || (item.busy && !item.content)) return
    if (item.onPress) item.onPress()
    else setOpenKey((current) => (current === item.key ? null : item.key))
  }

  return (
    <div className={classes.anchor}>
      {open && (
        <div className={classes.flyout} role='region' aria-label={open.label}>
          <div className={classes.flyoutHeader}>{open.label}</div>
          <div className={classes.flyoutBody}>{open.content}</div>
        </div>
      )}
      <div className={classes.rail}>
        {items.map((item) => (
          <Tooltip
            key={item.key}
            title={item.disabled ? item.disabledReason : item.label}
            placement='left'
            classes={{ tooltip: classes.pill }}
          >
            <button
              type='button'
              className={[
                classes.button,
                item.tone === 'amber' ? classes.buttonAmber : '',
                open?.key === item.key || item.live || item.on ? classes.buttonLive : '',
                item.tone === 'neutral' ? classes.buttonNeutral : '',
                item.disabled ? classes.buttonDisabled : '',
              ].join(' ')}
              onClick={() => press(item)}
              aria-label={item.label}
              aria-pressed={item.on}
              aria-disabled={item.disabled}
              aria-expanded={item.content ? open?.key === item.key : undefined}
            >
              {item.busy ? <CircularProgress size={20} color='inherit' /> : item.icon}
              {item.badge > 0 && <span className={classes.badge}>{item.badge}</span>}
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  )
}

StudioRail.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node.isRequired,
      content: PropTypes.node,
      onPress: PropTypes.func,
      busy: PropTypes.bool,
      on: PropTypes.bool,
      tone: PropTypes.oneOf(['amber', 'neutral']),
      badge: PropTypes.number,
      live: PropTypes.bool,
      disabled: PropTypes.bool,
      disabledReason: PropTypes.string,
    }),
  ).isRequired,
}

export default StudioRail
